import type { AxiosInstance } from 'axios'
import axiosInstant from 'api/baseRequest'
import { Dialog } from 'primereact/dialog'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  confirmReservationQr,
  createApiErrorLookupResult,
  lookupReservationByQrToken,
} from './scanCodeApi'
import { LookupResultPanel } from './LookupResultPanel'
import { QrScannerPanel } from './QrScannerPanel'
import { ScanCheckInSuccessPanel } from './ScanCheckInSuccessPanel'
import { ScanConfirmPanelSkeleton } from './ScanConfirmPanelSkeleton'
import type { QrConfirmReservationData, QrLookupResult } from './types'
import './DialogScanCode.scss'
import ScanHeader from './ScanHeader'
import { useScanQrViewportHeight } from './useScanQrViewportHeight'

export type DialogScanCodeProps = {
  visible: boolean
  onHide: () => void
  /** Event name shown under the title */
  eventTitle?: string
  /** Event id (Mongo) for QR lookup */
  eventId: string
  /** Kept for API compatibility — no longer sent to confirm endpoint */
  staffTimeslotId?: string | null
  /** Called after a successful check-in (e.g. refresh list) */
  onCheckInSuccess?: () => void | Promise<void>
  /** Checked-in count for status chip */
  acceptedCurrent?: number
  acceptedTotal?: number
  /** Optional client (e.g. volunteerRequest for public volunteer check-in) */
  apiClient?: AxiosInstance
}

type Step = 'scan' | 'lookup' | 'success'

export default function DialogScanCode({
  visible,
  onHide,
  eventTitle,
  eventId,
  onCheckInSuccess,
  acceptedCurrent,
  acceptedTotal,
  apiClient = axiosInstant,
}: DialogScanCodeProps) {
  const { t } = useTranslation()
  const [step, setStep] = useState<Step>('scan')
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [lookupToken, setLookupToken] = useState<string | null>(null)
  const [lookupResult, setLookupResult] = useState<QrLookupResult | null>(null)
  const [acceptLoading, setAcceptLoading] = useState(false)
  const [successData, setSuccessData] =
    useState<QrConfirmReservationData | null>(null)
  const lookupGenRef = useRef(0)

  const reset = useCallback(() => {
    lookupGenRef.current++
    setStep('scan')
    setLoadingDetail(false)
    setLookupToken(null)
    setLookupResult(null)
    setAcceptLoading(false)
    setSuccessData(null)
  }, [])

  useEffect(() => {
    if (!visible) reset()
  }, [visible, reset])

  const resolveToken = useCallback(
    async (token: string) => {
      const trimmed = token.trim()
      if (!trimmed || !eventId) return

      setLoadingDetail(true)
      setLookupResult(null)
      const gen = ++lookupGenRef.current
      setStep('lookup')

      try {
        const result = await lookupReservationByQrToken(
          { token: trimmed, eventId },
          apiClient
        )
        if (lookupGenRef.current !== gen) return
        setLookupToken(trimmed)
        setLookupResult(result)
      } catch (err: any) {
        if (lookupGenRef.current !== gen) return
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          t('Could not load reservation. Try again.')

        setLookupResult(
          createApiErrorLookupResult(msg, {
            title: t('Invalid QR Code'),
            subtitle: t('This QR code cannot be used.'),
          })
        )
      } finally {
        if (lookupGenRef.current === gen) setLoadingDetail(false)
      }
    },
    [apiClient, eventId, t]
  )

  const handleClose = useCallback(() => {
    reset()
    onHide()
  }, [onHide, reset])

  const backToScan = useCallback(() => {
    lookupGenRef.current++
    setStep('scan')
    setLookupResult(null)
    setLookupToken(null)
    setSuccessData(null)
    setLoadingDetail(false)
  }, [])

  const runCheckIn = useCallback(async () => {
    const token = lookupToken?.trim()
    if (!token || !eventId) return

    setAcceptLoading(true)

    try {
      const result = await confirmReservationQr({ token, eventId }, apiClient)
      if (result.kind === 'rejected') {
        setLookupResult(result.result)
        return
      }
      setSuccessData(result.data)
      setStep('success')
      try {
        await onCheckInSuccess?.()
      } catch {
        /* optional callback */
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        t('Check-in failed. Try again.')
      setLookupResult(
        createApiErrorLookupResult(msg, {
          title: t('Check-in could not be completed.'),
          subtitle: t('Please try again.'),
        })
      )
    } finally {
      setAcceptLoading(false)
    }
  }, [apiClient, eventId, lookupToken, onCheckInSuccess, t])

  const scannerActive = visible && step === 'scan'

  useScanQrViewportHeight(visible)

  return (
    <Dialog
      visible={visible}
      onHide={handleClose}
      showHeader={false}
      closable={false}
      draggable={false}
      resizable={false}
      modal
      className="scan-qr-dialog scan-qr-dialog--fullscreen mobile-select-branch-dialog m-0 shadow-none border-noround"
      contentClassName="p-0 h-full overflow-hidden"
    >
      <div className="scan-qr-root h-full">
        <ScanHeader
          onHeaderClose={handleClose}
          eventTitle={eventTitle}
          checkedInCount={acceptedCurrent}
          registeredCount={acceptedTotal}
        />

        <div className="scan-qr-body">
          {step === 'success' && successData ? (
            <ScanCheckInSuccessPanel data={successData} onClose={backToScan} />
          ) : step === 'lookup' && loadingDetail ? (
            <ScanConfirmPanelSkeleton />
          ) : step === 'lookup' && lookupResult ? (
            <LookupResultPanel
              result={lookupResult}
              checkInLoading={acceptLoading}
              onCheckIn={() => void runCheckIn()}
              onBack={backToScan}
            />
          ) : (
            <QrScannerPanel
              active={scannerActive}
              onDecodedPayload={resolveToken}
            />
          )}
        </div>
      </div>
    </Dialog>
  )
}
